import React, { useMemo, useState } from 'react';
import Box from '@amzn/meridian/box';
import Text from '@amzn/meridian/text';
import AssociateSidebar from '../assignments/components/AssociateSidebar';
import MetricsBar from '../assignments/components/MetricsBar';
import ProcessPathSection from '../assignments/components/ProcessPathSection';
import {
  AssignmentAssociate,
  AssignmentsMetrics,
  ProcessPath,
} from '../assignments/models';
import {
  filterUnassignedAssociates,
  isAssigned,
} from '../assignments/utils';

/**
 * Flattened, single-half view of a published plan.
 *
 * The backend `GetStationAssignmentsResponse` carries both halves of the
 * shift (`firstHalfStationAssignment` / `secondHalfStationAssignment`). The
 * read-only published screen renders one half at a time, so the container
 * resolves the active half and passes this flat view-model down — the
 * screen never reaches into the half-nested response itself.
 */
export interface PublishedAssignmentView {
  associates: AssignmentAssociate[];
  processPaths: ProcessPath[];
  metrics: AssignmentsMetrics;
  lastModifiedBy: string;
  lastModifiedDate: string;
}

/**
 * Props for {@link PublishedAssignmentScreen}.
 *
 * `searchQuery` and `onSearchChange` are owned by the container so the
 * screen stays a pure render of the supplied response. `planId` is part
 * of the screen's contract for future use (e.g. wiring deep links from
 * within the screen) even though it does not directly drive the current
 * render — the page header, which displays the Plan_Id, is composed by
 * the container, not by this screen.
 */
export interface PublishedAssignmentScreenProps {
  response: PublishedAssignmentView;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  planId: string;
}

/**
 * Presentational screen for the Published Assignment view.
 *
 * Renders the same layout as the editable Assignments page (left-column
 * sidebar of unassigned associates, top-of-page metrics bar, stacked
 * process-path sections, last-modified footer) but in read-only mode:
 *
 *   - `AssociateSidebar` is rendered with `readOnly` so per-row checkboxes
 *     and the inline "Assign to..." button are suppressed (Req 9.1, 9.2).
 *     The search field, search-clear control, filter button, and
 *     unassigned-count badge remain functional (Req 10.5).
 *   - Each `ProcessPathSection` is rendered with `readOnly`, which the
 *     section forwards to its `WorkstationCard` and `IndirectAssociateRow`
 *     children so the "Add associate" footer button and per-row remove
 *     controls are suppressed (Req 11.1, 11.2, 11.3).
 *   - No Verify, Submit, Publish, or workstation-picker modal is rendered
 *     (Req 12.1, 12.2, 12.3). No prop or rendered control reaches Redux
 *     (Req 12.4) — the screen is a pure renderer of the supplied response.
 *
 * The unassigned-associate pool is derived from the supplied response using
 * the same pure helpers (`isAssigned`, `filterUnassignedAssociates`) as the
 * editable Assignments page so the filter semantics are bit-for-bit
 * identical.
 *
 * The page header (title, back-navigation, plan metadata) is composed by
 * the container — it is intentionally not rendered here.
 */
const PublishedAssignmentScreen: React.FC<PublishedAssignmentScreenProps> = ({
  response,
  searchQuery,
  onSearchChange,
}) => {
  // Each process path defaults to expanded on first render, mirroring the
  // editable Assignments page. The collapse/expand toggle remains
  // functional in read-only mode because it is display-only — it does not
  // mutate plan data (design.md → "readOnly" forwarding rules).
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>(
    () => {
      const initial: Record<string, boolean> = {};
      response.processPaths.forEach((path) => {
        initial[path.fclmFunctionId] = true;
      });
      return initial;
    },
  );

  const processPaths: ProcessPath[] = response.processPaths;

  /**
   * The unassigned pool: every associate from the canonical master list
   * that does not appear in any workstation or indirect-path list.
   */
  const unassignedAssociates: AssignmentAssociate[] = useMemo(
    () => response.associates.filter((a) => !isAssigned(a, response.processPaths)),
    [response.associates, response.processPaths],
  );

  const filteredUnassignedAssociates = useMemo(
    () => filterUnassignedAssociates(unassignedAssociates, searchQuery),
    [unassignedAssociates, searchQuery],
  );

  const handleToggleExpanded = (processPathId: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [processPathId]: !(prev[processPathId] ?? true),
    }));
  };

  return (
    <Box className="assignments-page" spacingInset="400">
      <div className="assignments-page__layout">
        {/* Left column: unassigned associate sidebar in read-only mode. */}
        <div className="assignments-page__sidebar">
          <AssociateSidebar
            readOnly
            unassignedAssociates={filteredUnassignedAssociates}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
          />
        </div>

        {/* Right column: metrics bar, stacked process-path sections. */}
        <div className="assignments-page__main">
          <MetricsBar metrics={response.metrics} />

          <div className="assignments-page__sections">
            {processPaths.map((path) => (
              <ProcessPathSection
                key={path.fclmFunctionId}
                readOnly
                processPath={path}
                expanded={expandedPaths[path.fclmFunctionId] ?? true}
                onToggleExpanded={handleToggleExpanded}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer: Last Modified info. No Verify, Submit, or Publish action. */}
      <div className="assignments-page__footer">
        <Text
          color="secondary"
          data-testid="published-assignment-last-modified"
        >
          Last Modified: {response.lastModifiedDate} ({response.lastModifiedBy}
          )
        </Text>
      </div>
    </Box>
  );
};

export default PublishedAssignmentScreen;
